import { Tooltip, type TooltipPlacement } from '@trustoo/components/Tooltip/v2'
import React from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

const TOOLTIP_PLACEMENTS: { placement: TooltipPlacement; label: string }[] = [
    { placement: 'top', label: 'top' },
    { placement: 'top-start', label: 'top-start' },
    { placement: 'top-end', label: 'top-end' },
    { placement: 'bottom', label: 'bottom' },
    { placement: 'left', label: 'left' },
    { placement: 'right', label: 'right' },
]

const TOOLTIP_SUPPORTING =
    'Tooltips are used to describe or identify an element. In most scenarios, tooltips help the user understand meaning, function or alt-text.'

const TOOLTIP_USAGE = `import { Tooltip } from '@trustoo/components/Tooltip/v2'

<Tooltip title="This is a tooltip">
    <button type="button">Hover me</button>
</Tooltip>

// With supporting text + placement
<Tooltip
    title="This is a tooltip"
    supportingText="Tooltips describe or identify an element."
    placement="bottom">
    <InfoIcon />
</Tooltip>`

// A help "?" affordance is just a Tooltip wrapping any icon button — no
// dedicated component needed. The dev supplies whatever glyph they want.
const TOOLTIP_HELP_USAGE = `import { Tooltip } from '@trustoo/components/Tooltip/v2'

<label className="flex items-center gap-spacing-xs">
    Email address
    <Tooltip title="We'll never share your email address.">
        <button type="button" aria-label="Help">
            <HelpCircleIcon />
        </button>
    </Tooltip>
</label>`

// Inline help-circle glyph used by the real-world demo below. In product code
// the dev passes whatever icon they need as the Tooltip trigger.
const HelpCircleIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="size-4">
        <circle cx="12" cy="12" r="9.25" stroke="currentColor" strokeWidth="2" />
        <path
            d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M12 17h.01"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

export const TooltipShowcase: React.FC = () => (
    <Section
        title="Tooltip (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Tooltip</span> set
                (ComponentSet <InlineCode>5:45087</InlineCode>). A dark hover bubble built on{' '}
                <InlineCode>@radix-ui/react-tooltip</InlineCode> — it opens on hover or keyboard
                focus, portals (never clipped) and is collision-aware. Import v2 via{' '}
                <InlineCode>@trustoo/components/Tooltip/v2</InlineCode> (net-new — no v1 barrel
                yet).
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Placements</h3>
                <p className="text-xs text-text-tertiary">
                    Forced <InlineCode>open</InlineCode> to show the arrow positions; in use they
                    appear on hover or keyboard focus.
                </p>
                <div className="grid grid-cols-3 gap-x-spacing-9xl gap-y-spacing-9xl rounded-radius-xs border border-border-secondary bg-base-white px-spacing-7xl py-spacing-8xl">
                    {TOOLTIP_PLACEMENTS.map(({ placement, label }) => (
                        <div key={placement} className="flex justify-center">
                            <Tooltip open title="This is a tooltip" placement={placement}>
                                <span className="inline-flex items-center rounded-radius-xs border border-dashed border-border-primary px-spacing-lg py-spacing-md text-xs font-medium text-text-tertiary">
                                    {label}
                                </span>
                            </Tooltip>
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Supporting text</h3>
                <div className="grid grid-cols-2 gap-spacing-4xl rounded-radius-xs border border-border-secondary bg-base-white px-spacing-4xl py-spacing-7xl">
                    <div className="flex justify-center">
                        <Tooltip open title="This is a tooltip" placement="top">
                            <span className="inline-flex items-center rounded-radius-xs border border-dashed border-border-primary px-spacing-lg py-spacing-md text-xs font-medium text-text-tertiary">
                                title only
                            </span>
                        </Tooltip>
                    </div>
                    <div className="flex justify-center">
                        <Tooltip
                            open
                            title="This is a tooltip"
                            supportingText={TOOLTIP_SUPPORTING}
                            placement="top">
                            <span className="inline-flex items-center rounded-radius-xs border border-dashed border-border-primary px-spacing-lg py-spacing-md text-xs font-medium text-text-tertiary">
                                + supporting text
                            </span>
                        </Tooltip>
                    </div>
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">
                    Real-world usage (hover or focus)
                </h3>
                <p className="text-xs text-text-tertiary">
                    The trigger is whatever you pass as the single child — a help{' '}
                    <InlineCode>?</InlineCode> icon, a button, or a text span. Wrap an icon in a{' '}
                    <InlineCode>&lt;button&gt;</InlineCode> so it stays keyboard-reachable.
                </p>
                <div className="flex flex-col gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-3xl">
                    <div className="max-w-width-xs space-y-spacing-xl">
                        <label className="flex items-center gap-spacing-xs text-sm font-medium text-text-secondary">
                            Email address
                            <Tooltip title="We'll never share your email address.">
                                <button
                                    type="button"
                                    aria-label="Help"
                                    className="inline-flex size-4 items-center justify-center rounded-radius-full text-text-quaternary hover:text-text-tertiary focus-visible:text-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/24">
                                    <HelpCircleIcon />
                                </button>
                            </Tooltip>
                        </label>
                        <label className="flex items-center gap-spacing-xs text-sm font-medium text-text-secondary">
                            API rate limit
                            <Tooltip
                                title="Requests per minute"
                                supportingText="Calls beyond this limit are throttled for 60 seconds."
                                placement="right">
                                <button
                                    type="button"
                                    aria-label="Help"
                                    className="inline-flex size-4 items-center justify-center rounded-radius-full text-text-quaternary hover:text-text-tertiary focus-visible:text-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/24">
                                    <HelpCircleIcon />
                                </button>
                            </Tooltip>
                        </label>
                    </div>
                    <div className="flex flex-wrap items-center gap-spacing-3xl">
                        <Tooltip title="This is a tooltip">
                            <button
                                type="button"
                                className="rounded-radius-sm bg-brand-500 px-spacing-xl py-spacing-md text-sm font-semibold text-base-white hover:bg-brand-700">
                                Hover me
                            </button>
                        </Tooltip>
                        <Tooltip
                            title="Saved automatically"
                            supportingText="Your changes are stored as you type."
                            placement="bottom">
                            <button
                                type="button"
                                className="rounded-radius-sm border border-border-primary px-spacing-xl py-spacing-md text-sm font-medium text-text-secondary hover:bg-background-bg-primary_hover">
                                Hover or focus this button
                            </button>
                        </Tooltip>
                    </div>
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={TOOLTIP_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>title</InlineCode>, <InlineCode>supportingText</InlineCode>,{' '}
                    <InlineCode>placement</InlineCode> (top · top-start · top-end · bottom · left ·
                    right), <InlineCode>arrow</InlineCode>, and a controlled{' '}
                    <InlineCode>open</InlineCode> (force-show). The single child is the trigger
                    (rendered via Radix <InlineCode>asChild</InlineCode>).
                </p>
                <div className="mt-spacing-xl">
                    <CodeSnippet code={TOOLTIP_HELP_USAGE} />
                </div>
            </div>
        </div>
    </Section>
)
